import { useEffect, useState } from 'react';
import {
  styled,
  Container,
  AncestryLineChart,
  Text,
  Button,
  Stack,
  Select,
  LineChartProps,
  useMediaQuery,
  useTheme,
} from '@/theme/index';
import { suffixToNumbers } from '@/utils/index';

export interface ChartFormattedData {
  date?: string;
  value: number;
  direction?: string;
  score?: number;
}
export interface NamePopularityProps {
  /** Gender name used as display value */
  name: string;
  /** Name Popularity values list associated to the specific Gender */
  data: ChartFormattedData[];
}

export interface CategoryProps {
  /** Category name used as display value */
  name: string;

  /** Category Display */
  show: boolean;
}

export interface NamePopularityGraphProps {
  /** Name Popularity values list associated to the specific Gender */
  data: NamePopularityProps[] | null;
  /** List of categories used on the graphs, as the xAxis or yAxis, mostly used to reference data points categories in a graph */
  categories?: string[];

  /** Name of the User in the Report to be displayed as the title */
  firstName: string;

  category: CategoryProps;

  /** Report owner year of birth */
  yearOfBirth: string;

  /** Callback fired when the user clicks on the **Male** button. */
  onMaleBtnClick: () => void;

  /** Callback fired when the user clicks on the **Female** button. */
  onFemaleBtnClick: () => void;
}

export interface DropDownProps {
  value: string;
  display: string;
}

const HeaderTitle = styled('div')(({ theme }) => ({
  fontSize: '20px',
  textAlign: 'center',
  marginTop: '20px',
  color: theme.palette.success.main,

  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    textAlign: 'left',
  },
}));

const PopularityContainer = styled('div')(({ theme }) => ({
  color: theme.palette.primary.main,
  borderRadius: '6px',
  backgroundColor: theme.palette.secondary.light,
  padding: '18px',
  textAlign: 'left',
  fontSize: '18px',
}));

const NoDataContainer = styled('div')(({ theme }) => ({
  padding: '50px',
  textAlign: 'center',
  fontSize: '20px',

  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
    padding: '0',
    height: '20px',
    marginTop: '30px',
    textAlign: 'left',
  },
}));

const FooterTitle = styled('div')(({ theme }) => ({
  fontSize: '18px',
  marginBottom: '10px',
  textAlign: 'center',
  color: theme.palette.success.main,

  [theme.breakpoints.down('sm')]: {
    fontSize: '16px',
    textAlign: 'left',
    marginTop: '30px',
  },
}));

const MorePopularTitle = styled('div')(() => ({
  fontSize: '12px',
  fontWeight: '600',
  textAlign: 'center',
}));

const LessPopularTitle = styled('div')(() => ({
  fontSize: '12px',
  fontWeight: '600',
  textAlign: 'center',
}));

const GenderContainer = styled('div')(() => ({
  fontSize: '16px',
  marginTop: '20px',
}));

const YearContainer = styled('div')(({ theme }) => ({
  fontSize: '16px',
  color: theme.palette.primary.main,
  marginTop: '20px',
}));

const DropDownSelect = styled(Select)(({ theme }) => ({
  width: '170px',
  color: theme.palette.primary.main,
  fontWeight: 'bold',
}));

export function NamePopularityGraph(chartData: NamePopularityGraphProps) {
  const [lineChartData, setLineChartData] = useState<LineChartProps>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dropdownData, setDropdownData] = useState<DropDownProps[]>();
  const [commonName, setCommonName] = useState<string>('');

  function selectColor(data: string) {
    return data === chartData.category.name ? 'contained' : 'outlined';
  }

  function handleOptionClicked(data: string) {
    setCommonName(data);
  }

  function selectData(): DropDownProps {
    const data = commonName?.split('|');

    return {
      value: (data && data[0]) || '',
      display: (data && suffixToNumbers(Number(data[1]))) || '',
    };
  }

  useEffect(() => {
    if (chartData) {
      const selectData =
        (chartData.data &&
          chartData.data[0]?.data?.map((category: ChartFormattedData) => ({
            value: `${category.date}|${category.score}`,
            display: category.date?.toString() || '',
          }))) ||
        [];

      const formattedData = {
        horizontalLabels:
          chartData.categories?.map((category: string) => ({
            label: category,
          })) || [],
        data: chartData.data || [],
      };

      setLineChartData(formattedData);
      setDropdownData(selectData);
      const commonNameForYear = selectData.find(
        (data) => data.display === chartData.yearOfBirth
      );

      setCommonName(commonNameForYear?.value || selectData[0]?.value);
    }
  }, [chartData]);

  return (
    <Container>
      <HeaderTitle>
        <Text
          variant="h3"
          fontWeight="normal"
          fontSize={isMobile ? '16px' : '20px'}
        >
          Popularity of the name
          <span style={{ fontWeight: 'bold' }}> {chartData.firstName} </span>
          over time
        </Text>
      </HeaderTitle>

      {isMobile && chartData.category.show && (
        <PopularityContainer>
          <Text component="span" fontSize="18px" marginBottom="15px">
            <span style={{ fontWeight: 'bold' }}> {chartData.firstName} </span>
            was the{' '}
            <span style={{ fontWeight: 'bold' }}>
              {selectData().display}
            </span>{' '}
            most common name of{' '}
            <span style={{ fontWeight: 'bold' }}>{selectData().value}</span>
          </Text>
        </PopularityContainer>
      )}

      {!isMobile && chartData.category.show && (
        <div style={{ display: 'flex' }}>
          <Stack flexDirection="column" justifyContent="space-evenly">
            <MorePopularTitle>
              More
              <br />
              Popular
            </MorePopularTitle>
            <LessPopularTitle>
              Less
              <br />
              Popular
            </LessPopularTitle>
          </Stack>

          {lineChartData && (
            <AncestryLineChart
              data={lineChartData.data}
              horizontalLabels={lineChartData.horizontalLabels}
              options={{ hideLegend: true }}
              dob={chartData.yearOfBirth}
            />
          )}
        </div>
      )}

      {!chartData.category.show && (
        <NoDataContainer>
          There is no <b>{chartData.category.name}</b> data to display
        </NoDataContainer>
      )}

      <FooterTitle>
        Sort name popularity by gender
        <GenderContainer>
          <Button
            color="success"
            variant={selectColor('Male')}
            sx={{
              width: isMobile ? '50%' : '30%',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }}
            onClick={chartData.onMaleBtnClick}
          >
            Male
          </Button>

          <Button
            variant={selectColor('Female')}
            color="success"
            sx={{
              width: isMobile ? '50%' : '30%',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
            onClick={chartData.onFemaleBtnClick}
          >
            Female
          </Button>
        </GenderContainer>
      </FooterTitle>

      {isMobile && dropdownData && (
        <YearContainer>
          <div style={{ marginBottom: '20px' }}>Select a year</div>
          <DropDownSelect
            id="name-popularity-dropdown"
            name="name-popularity-dropdown"
            value={commonName}
            options={dropdownData}
            placeholder="Year"
            onOptionSelect={(selectedValue: string) =>
              handleOptionClicked(selectedValue)
            }
          />
        </YearContainer>
      )}
    </Container>
  );
}
